/**
 * LocalPulseSection — "Your Local Pulse" hub.
 *
 * Auto-detects (or lets visitor pick) a location, then surfaces:
 *   • Local Stories        · /explore/{slug}
 *   • Events Near You      · /events?place={slug}
 *   • Find or Offer Help   · /find-help?place={slug}
 *   • Community Resources  · /resources?place={slug}
 *
 * Uses the existing `useGeo` hook so we don't fight the app-wide
 * location state. If nothing is set, we default to a friendly
 * placeholder city so the UI still looks alive.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, ArrowRight, BookOpen, Calendar, HandHeart, Users,
} from "lucide-react";
import { useGeo } from "@/lib/useGeo";

const TILES = [
  {
    key: "stories",
    icon: BookOpen,
    label: "Local Stories",
    blurb: "Real people. Real places. Inspiring stories from your community.",
    color: "bg-[#1E64C8]",
    route: (slug) => `/publisher/hub?place=${slug}`,
  },
  {
    key: "events",
    icon: Calendar,
    label: "Events Near You",
    blurb: "Discover community events, meetups and local happenings.",
    color: "bg-[#F58220]",
    route: (slug) => `/events?place=${slug}`,
  },
  {
    key: "help",
    icon: HandHeart,
    label: "Find or Offer Help",
    blurb: "Lend a hand or get support from people near you.",
    color: "bg-emerald-600",
    route: (slug) => `/find-help?place=${slug}`,
  },
  {
    key: "resources",
    icon: Users,
    label: "Community Resources",
    blurb: "Find local services, businesses and helpful resources.",
    color: "bg-[#7B4A9C]",
    route: (slug) => `/explore/${slug}`,
  },
];

function displayLocation(geo) {
  if (!geo) return "Lake Forest, California";
  const bits = [geo.city, geo.state, geo.country].filter(Boolean);
  if (bits.length) return bits.slice(0, 2).join(", ");
  return "Lake Forest, California";
}

function slugFor(geo) {
  const city = geo?.city || "lake-forest";
  return city.toLowerCase().replace(/\s+/g, "-");
}

export default function LocalPulseSection() {
  const { geo } = useGeo();
  const location = useMemo(() => displayLocation(geo), [geo]);
  const slug = useMemo(() => slugFor(geo), [geo]);

  return (
    <section
      className="w-full"
      style={{ background: "#F0F5FA" }}
      data-testid="home-local-pulse"
    >
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-14">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase text-xs font-mono font-semibold tracking-[0.2em] text-[#50586C] mb-2">
            Right around you
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1226] tracking-tight leading-snug flex items-center gap-3 flex-wrap">
            Your Local Pulse
            <span
              className="inline-flex items-center gap-1.5 text-sm text-[#1E64C8] font-semibold bg-white rounded-full px-3 py-1 shadow-sm"
              data-testid="home-local-pulse-city"
            >
              <MapPin className="w-3.5 h-3.5" /> {location}
            </span>
          </h2>
          <p className="text-base text-[#50586C] mt-1.5">
            Stories, events, causes and helpful people right around you.
          </p>
        </div>
        <Link
          to={`/explore/${slug}`}
          className="text-sm font-semibold text-[#1E64C8] hover:text-[#164FA0] inline-flex items-center gap-0.5"
          data-testid="home-local-pulse-more"
        >
          See more in {location.split(",")[0]} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </header>

      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TILES.map((t) => (
          <li key={t.key} data-testid={`home-local-pulse-tile-${t.key}`}>
            <Link
              to={t.route(slug)}
              className="group block h-full rounded-2xl bg-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 overflow-hidden"
            >
              <div
                className="h-28 w-full relative flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #FDFBF7 0%, #F0F5FA 100%)" }}
              >
                <span
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${t.color} text-white shadow-md`}
                >
                  <t.icon className="w-6 h-6" />
                </span>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-medium text-[#0B1226] leading-snug">
                  {t.label}
                </h3>
                <p className="mt-1 text-sm text-[#50586C] leading-relaxed">
                  {t.blurb}
                </p>
                <span className="mt-2 inline-flex items-center gap-0.5 text-xs font-semibold text-[#1E64C8] group-hover:text-[#164FA0]">
                  Open <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      </div>
    </section>
  );
}
